import { createSlice } from '@reduxjs/toolkit';

import {
    createTieringConfig,
    fetchActiveTieringConfig,
    fetchDataDictionaryColumns,
    fetchTieringConfigById,
    fetchTieringConfigs
} from './hospital-tiering-api';
import type { DataDictionaryColumn, HospitalTieringConfig, HospitalTieringConfigListItem } from '@/types/hospital-tiering';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';

export interface HospitalTieringState {
    configs: HospitalTieringConfigListItem[];
    activeConfig: HospitalTieringConfig | null;
    currentConfig: HospitalTieringConfig | null;
    dataDictionaryColumns: DataDictionaryColumn[];
    total: number;
    page: number;
    limit: number;
    configsLoading: boolean;
    configsError: string | null;
    activeConfigLoading: boolean;
    activeConfigError: string | null;
    currentConfigLoading: boolean;
    currentConfigError: string | null;
    dataDictionaryColumnsLoading: boolean;
    dataDictionaryColumnsError: string | null;
    createConfigLoading: boolean;
    createConfigError: string | null;
}

const initialState: HospitalTieringState = {
    configs: [],
    activeConfig: null,
    currentConfig: null,
    dataDictionaryColumns: [],
    total: 0,
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
    configsLoading: false,
    configsError: null,
    activeConfigLoading: false,
    activeConfigError: null,
    currentConfigLoading: false,
    currentConfigError: null,
    dataDictionaryColumnsLoading: false,
    dataDictionaryColumnsError: null,
    createConfigLoading: false,
    createConfigError: null
};

const hospitalTieringSlice = createSlice({
    name: 'hospitalTiering',
    initialState,
    reducers: {
        clearHospitalTieringErrors: (state) => {
            state.configsError = null;
            state.activeConfigError = null;
            state.currentConfigError = null;
            state.dataDictionaryColumnsError = null;
            state.createConfigError = null;
        },
        clearCurrentTieringConfig: (state) => {
            state.currentConfig = null;
            state.currentConfigError = null;
        },
        clearActiveTieringConfig: (state) => {
            state.activeConfig = null;
            state.activeConfigError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActiveTieringConfig.pending, (state) => {
                state.activeConfigLoading = true;
                state.activeConfigError = null;
                state.activeConfig = null;
            })
            .addCase(fetchActiveTieringConfig.fulfilled, (state, action) => {
                state.activeConfigLoading = false;
                state.activeConfig = action.payload;
                state.activeConfigError = null;
            })
            .addCase(fetchActiveTieringConfig.rejected, (state, action) => {
                state.activeConfigLoading = false;
                state.activeConfigError = action.payload ?? 'Failed to fetch active tiering config';
            })
            .addCase(fetchTieringConfigs.pending, (state) => {
                state.configsLoading = true;
                state.configsError = null;
            })
            .addCase(fetchTieringConfigs.fulfilled, (state, action) => {
                state.configsLoading = false;
                state.configs = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
                state.configsError = null;
            })
            .addCase(fetchTieringConfigs.rejected, (state, action) => {
                state.configsLoading = false;
                state.configsError = action.payload ?? 'Failed to fetch tiering configs';
            })
            .addCase(fetchTieringConfigById.pending, (state) => {
                state.currentConfigLoading = true;
                state.currentConfigError = null;
            })
            .addCase(fetchTieringConfigById.fulfilled, (state, action) => {
                state.currentConfigLoading = false;
                state.currentConfig = action.payload;
                state.currentConfigError = null;
            })
            .addCase(fetchTieringConfigById.rejected, (state, action) => {
                state.currentConfigLoading = false;
                state.currentConfigError = action.payload ?? 'Failed to fetch tiering config';
            })
            .addCase(fetchDataDictionaryColumns.pending, (state) => {
                state.dataDictionaryColumnsLoading = true;
                state.dataDictionaryColumnsError = null;
                state.dataDictionaryColumns = [];
            })
            .addCase(fetchDataDictionaryColumns.fulfilled, (state, action) => {
                state.dataDictionaryColumnsLoading = false;
                state.dataDictionaryColumns = action.payload;
                state.dataDictionaryColumnsError = null;
            })
            .addCase(fetchDataDictionaryColumns.rejected, (state, action) => {
                state.dataDictionaryColumnsLoading = false;
                state.dataDictionaryColumnsError = action.payload ?? 'Failed to fetch data dictionary columns';
            })
            .addCase(createTieringConfig.pending, (state) => {
                state.createConfigLoading = true;
                state.createConfigError = null;
            })
            .addCase(createTieringConfig.fulfilled, (state, action) => {
                state.createConfigLoading = false;
                state.createConfigError = null;
                state.currentConfig = action.payload;
                state.configs = [
                    {
                        id: action.payload.id,
                        hospitalId: action.payload.hospitalId,
                        version: action.payload.version,
                        status: action.payload.status,
                        createdAt: action.payload.createdAt,
                        activatedAt: action.payload.activatedAt
                    },
                    ...state.configs
                ];
                state.total += 1;
            })
            .addCase(createTieringConfig.rejected, (state, action) => {
                state.createConfigLoading = false;
                state.createConfigError = action.payload ?? 'Failed to create tiering config';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearHospitalTieringErrors, clearCurrentTieringConfig, clearActiveTieringConfig } = hospitalTieringSlice.actions;
export default hospitalTieringSlice.reducer;
